import { AccessRule, AccessRuleException, Prisma } from '@prisma/client';
import { useMutation, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

import { DOMAIN_ACCESS_RULE_PATH, DOMAIN_ACCESS_URL_ROOT } from '@app/domain-access-interfaces';
import { CommonFindResult } from '@app/infra-interfaces';
import { Axios } from '@app/web-components';

import { accessKeys } from '../../access/data';

import { accessRulesKeys } from './access-rule';

export const accessRuleExceptionKeys = {
  all: ['access-rule-exceptions'] as const,
  lists: () => [...accessRuleExceptionKeys.all, 'list'] as const,
  list: (filters: object) => [...accessRuleExceptionKeys.lists(), filters] as const,
  item: (id: number) => [...accessRuleExceptionKeys.all, 'item', id],
};

export const useFindAccessRuleExceptions = <T>(
  args: Prisma.AccessRuleExceptionFindManyArgs,
  options?: Omit<UseQueryOptions<CommonFindResult<T>>, 'queryKey' | 'queryFn'>,
) => {
  const axios = Axios.useAxiosApi();

  return useQuery<CommonFindResult<T>>({
    ...options,
    queryKey: accessRuleExceptionKeys.list(args),
    queryFn: () =>
      axios
        .post(`/${DOMAIN_ACCESS_URL_ROOT}/${DOMAIN_ACCESS_RULE_PATH}/exceptions/find`, args)
        .then((response) => response.data),
  });
};

export type AccessRuleExceptionWithDetail = AccessRuleException & { accessRule: AccessRule };

export const useGetUserAccessRuleExceptions = (
  wwid: string,
  options?: Omit<UseQueryOptions<CommonFindResult<AccessRuleExceptionWithDetail>>, 'queryKey' | 'queryFn'>,
) => {
  const axios = Axios.useAxiosApi();

  return useQuery<CommonFindResult<AccessRuleException & { accessRule: AccessRule }>>({
    ...options,
    queryKey: ['user', 'access-rule-exceptions', wwid],
    queryFn: () => axios.get(`/user/${wwid}/access-rule-exceptions`).then((response) => response.data),
  });
};

export const useUpdateUserAccessRuleExceptions = (wwid: string) => {
  const axios = Axios.useAxiosApi();
  const queryClient = useQueryClient();

  return useMutation<AxiosResponse<AccessRuleException[]>, unknown, { justification: string; rules: number[] }>({
    mutationFn: (body) => axios.patch(`/user/${wwid}/access-rule-exceptions`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accessKeys.all });
    },
  });
};

export const useRemoveAccessRuleException = () => {
  const axios = Axios.useAxiosApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => {
      return axios.delete(`/${DOMAIN_ACCESS_URL_ROOT}/${DOMAIN_ACCESS_RULE_PATH}/exceptions/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accessRuleExceptionKeys.all });
      queryClient.invalidateQueries({ queryKey: accessRulesKeys.all });
      queryClient.invalidateQueries({ queryKey: accessKeys.all });
    },
  });
};
